/* eslint-disable react/prop-types */

import { useState, useEffect, useRef } from "react";
import dayjs from "@shared/dayjs";
import DateSelector from "@shared/v2/DateSelector";
import { useClickOutside } from "@shared/hookHelpers";
import { InlineEditIcon } from "../icons";
import Switcher from "./Switcher";

const PICKER_FORMAT = "MM/DD/YYYY";
const QUERY_FORMAT = "YYYY-MM-DD";

const format = (dateValue) => dayjs(dateValue).format("MM-DD-YYYY");

const toPickerValue = (dateValue) =>
  dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue).format(PICKER_FORMAT) : "";

const DateCell = ({ value, isEditable, onChange, justify }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const pickerRef = useRef(null);
  const pendingDateRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const iconType = <InlineEditIcon isEditable={isEditable} />;

  const blankNonEditableDisplay = <span>—</span>;
  const blankEditableDisplay = (
    <span data-cy="editable-blank-date-cell" className="tw-text-theme-text-button-color">
      Add
    </span>
  );
  const nonEditableDisplay = <span>{format(currentValue)}</span>;
  const editableDisplay = (
    <div data-cy="editable-display-date-cell">
      <span className="tw-mr-8px">{format(currentValue)}</span>
      {iconType}
    </div>
  );
  const isEmpty = !currentValue || currentValue.length === 0;

  const maybeToggleToInput = () => {
    if (!isEditing) {
      pendingDateRef.current = null;
      setIsEditing(true);
    }
  };

  const updateIfChanged = (newValue) => {
    if (newValue !== value) onChange(newValue);
  };

  const closeAndUpdateIfChanged = (formattedDate) => {
    const parsed = formattedDate ? dayjs(formattedDate, PICKER_FORMAT, true) : null;
    if (parsed?.isValid()) updateIfChanged(parsed.format(QUERY_FORMAT));
    pendingDateRef.current = null;
    setIsEditing(false);
  };

  useClickOutside(pickerRef, () => closeAndUpdateIfChanged(pendingDateRef.current));

  const input = (
    <div className="tw-relative">
      <span className="tw-mr-8px">{isEmpty ? "—" : format(currentValue)}</span>
      <div
        ref={pickerRef}
        className="tw-absolute tw-top-full tw-left-0 tw-z-1000 tw-w-[240px] tw-px-12px tw-pb-12px tw-bg-white tw-shadow-[0px_6px_20px_2px_rgba(51,51,51,0.16)]"
      >
        <DateSelector
          value={toPickerValue(value)}
          onChange={({ formattedDate, dateChangeMethod }) => {
            pendingDateRef.current = formattedDate;
            if (dateChangeMethod === "clicked" && formattedDate) closeAndUpdateIfChanged(formattedDate);
          }}
        />
      </div>
    </div>
  );

  let content = null;
  if (!isEditable && !isEmpty) {
    content = nonEditableDisplay;
  } else if (!isEditable && isEmpty) {
    content = blankNonEditableDisplay;
  } else if (isEditable && isEditing) {
    content = input;
  } else if (isEditable && !isEditing && isEmpty) {
    content = blankEditableDisplay;
  } else if (isEditable && !isEditing && !isEmpty) {
    content = editableDisplay;
  }

  return (
    <Switcher justify={justify} onClick={maybeToggleToInput} isActionable={isEditable && !isEditing}>
      {content}
    </Switcher>
  );
};

export default DateCell;
